import Button from "@components/Button";
import useDataStore from "@state/DataStore";
import { numberToCurrency } from "@utils/string";
import { useNavigate, useParams } from "react-router-dom";
import Alert from "@components/Alert";
import { useTranslation } from "react-i18next";
import useDeleteReservation from "./use-delete-reservation";
import useAcceptReservation from "./use-accept-reservation";

const Detail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const reservation = useDataStore((state) =>
    id
      ? state.reservations.find((res) => res.id === parseInt(id, 10))
      : undefined
  );

  if (!reservation) {
    throw new Error(t<string>("Reservation not found"));
  }

  const {
    deleteReservation,
    loading: deleteLoading,
    error: deleteError,
  } = useDeleteReservation();
  const {
    acceptReservation,
    loading: acceptLoading,
    error: acceptError,
  } = useAcceptReservation();

  const boat = useDataStore((state) =>
    id ? state.boats.find((res) => res.id === reservation.boatId) : undefined
  );

  const backHandler = () => {
    navigate(-1);
  };

  const acceptHandler = async () => {
    const res = await acceptReservation(reservation.id);
    if (res?.status === "success") {
      navigate(-1);
    }
  };

  const deleteHandler = async () => {
    const res = await deleteReservation(reservation.id);
    if (res?.status === "success") {
      navigate(-1);
    }
  };

  return (
    <div>
      <h2 className="text-4xl font-extrabold mb-4">
        {t("orderDetail", { id: reservation?.id })}
      </h2>
      <h3 className="text-3xl font-extrabold mb-4">
        {boat?.name} - {reservation.date.toLocaleDateString()}
      </h3>
      {deleteError && <Alert type="error">{deleteError}</Alert>}
      {acceptError && <Alert type="error">{acceptError}</Alert>}
      <div className="mb-6">
        <div className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {t("Name")}
        </div>
        <div>{reservation?.name ? reservation?.name : "-"}</div>
      </div>
      <div className="mb-6">
        <div className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {t("Phone")}
        </div>
        <div>{reservation?.phone ? reservation?.phone : "-"}</div>
      </div>
      <div className="mb-6">
        <div className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {t("Email")}
        </div>
        <div>{reservation?.email ? reservation?.email : "-"}</div>
      </div>
      <div className="mb-6">
        <div className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {t("Note")}
        </div>
        <div>{reservation?.text ? reservation?.text : "-"}</div>
      </div>
      <div className="mb-6">
        <div className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {t("Price")}
        </div>
        <div>{numberToCurrency(reservation.price)}</div>
      </div>
      <div className="flex flex-row gap-2">
        <Button
          onClick={backHandler}
          outline
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
              />
            </svg>
          }
        >
          {t("Back")}
        </Button>
        <Button
          onClick={deleteHandler}
          loading={deleteLoading}
          outline
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
              />
            </svg>
          }
        >
          {t("Remove")}
        </Button>
        {reservation.status === "draft" && (
          <Button
            onClick={acceptHandler}
            loading={acceptLoading}
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 12.75l6 6 9-13.5"
                />
              </svg>
            }
          >
            {t("Confirm")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default Detail;
